import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DataTable } from '@components/organisms/Documents/components/DataTable';
import { SourceBadge } from '@components/atoms/SourceBadge';
import { MaterialSource } from '@models/warehouse.model';
import { cn } from '@utils/cn';

type InventoryItem = {
  totalPrice?: number | null;
  unitPrice?: number | null;
  totalCost?: number | null;
  unitCost?: number | null;
  isOwnItem?: boolean;
  unassigned?: boolean;
  originalOrganizationName?: string;
  displayOrganizationName?: string;
  chargeToClient?: boolean;
  amount?: number;
  name?: string;
  equipmentId?: string;
  time?: string;
  taskName?: string;
  taskId?: string;
  costPerHour?: number | null;
  source?: 'WAREHOUSE' | 'MANUAL';
};

interface ByTaskTabProps {
  inventory: InventoryItem[];
  isDelegated: boolean;
}

const UNASSIGNED_ID = '__unassigned__';

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `$ ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  return new Date(dateString).toLocaleString('en-US', options);
};

type TaskGroup = {
  taskId: string;
  taskName: string;
  items: InventoryItem[];
  totalPrice: number;
  totalCost: number;
};

export const ByTaskTab: React.FC<ByTaskTabProps> = ({
  inventory,
  isDelegated,
}) => {
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  const toggleTask = (taskId: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const { taskGroups, showPriceColumns, grandTotalPrice, grandTotalCost } =
    useMemo(() => {
      const validItems = inventory.filter((item) => {
        const hasValidTotalPrice =
          item.totalPrice !== null &&
          item.totalPrice !== undefined &&
          !isNaN(item.totalPrice);
        const hasValidTotalCost =
          item.totalCost !== null &&
          item.totalCost !== undefined &&
          !isNaN(item.totalCost);
        return hasValidTotalPrice || hasValidTotalCost;
      });

      const hasChargeToClientItems = validItems.some(
        (item) => item.chargeToClient,
      );
      const showPrice = isDelegated && hasChargeToClientItems;

      const groupsMap = new Map<string, TaskGroup>();
      for (const item of validItems) {
        const key = item.taskId || UNASSIGNED_ID;
        const name = item.taskId ? item.taskName || '-' : 'Unassigned';
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            taskId: key,
            taskName: name,
            items: [],
            totalPrice: 0,
            totalCost: 0,
          });
        }
        const g = groupsMap.get(key)!;
        g.items.push(item);
        g.totalPrice += item.totalPrice || 0;
        g.totalCost += item.totalCost || 0;
      }

      const groups = Array.from(groupsMap.values()).sort((a, b) => {
        if (a.taskId === UNASSIGNED_ID) return 1;
        if (b.taskId === UNASSIGNED_ID) return -1;
        return a.taskName.localeCompare(b.taskName);
      });

      const gTotalPrice = groups.reduce((sum, g) => sum + g.totalPrice, 0);
      const gTotalCost = groups.reduce((sum, g) => sum + g.totalCost, 0);

      return {
        taskGroups: groups,
        showPriceColumns: showPrice,
        grandTotalPrice: gTotalPrice,
        grandTotalCost: gTotalCost,
      };
    }, [inventory, isDelegated]);

  const columns = useMemo(() => {
    const cols: any[] = [];
    if (showPriceColumns) {
      cols.push(
        {
          key: 'totalPrice',
          label: 'Total Price',
          className: 'col-span-1',
          render: (v: number) => formatCurrency(v),
        },
        {
          key: 'unitPrice',
          label: 'Unit Price',
          className: 'col-span-1',
          render: (v: number) => formatCurrency(v),
        },
      );
    }
    cols.push(
      {
        key: 'totalCost',
        label: 'Total Cost',
        className: 'col-span-1',
        render: (v: number) => formatCurrency(v),
      },
      {
        key: 'unitCost',
        label: 'Unit Cost',
        className: 'col-span-1',
        render: (v: number) => formatCurrency(v),
      },
      {
        key: 'amount',
        label: 'Amount',
        className: 'col-span-1',
        render: (v: number) => v ?? '-',
      },
      {
        key: 'name',
        label: 'Item',
        className: 'col-span-1 whitespace-nowrap',
        render: (v: string) => v || '-',
      },
      {
        key: 'type',
        label: 'Type',
        className: 'col-span-1',
        render: (_: any, row: InventoryItem) =>
          row.equipmentId ? 'Equipment' : 'Material',
      },
      {
        key: 'organization',
        label: 'Organization',
        className: 'col-span-1 whitespace-nowrap',
        render: (_: any, row: InventoryItem) => {
          if (!row.isOwnItem && row.originalOrganizationName && row.originalOrganizationName !== 'Unknown') {
            return row.originalOrganizationName;
          }
          if (row.unassigned && row.isOwnItem && row.displayOrganizationName) {
            return row.displayOrganizationName;
          }
          return '-';
        },
      },
      {
        key: 'source',
        label: 'Source',
        className: 'col-span-1',
        render: (_: any, row: InventoryItem) => (
          <SourceBadge
            source={
              row.source === 'WAREHOUSE'
                ? MaterialSource.WAREHOUSE
                : MaterialSource.MANUAL
            }
            size="sm"
          />
        ),
      },
      {
        key: 'time',
        label: 'Date',
        className: 'col-span-1',
        render: (v: string) => formatDate(v),
      },
    );
    return cols;
  }, [showPriceColumns]);

  const rawProfit = grandTotalPrice - grandTotalCost;

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">
        Showing{' '}
        <span className="font-semibold text-gray-700">{taskGroups.length}</span>{' '}
        task{taskGroups.length !== 1 ? 's' : ''}
      </p>

      {taskGroups.length === 0 ? (
        <div className="border border-gray-200 rounded-md p-8 text-center text-sm text-gray-500">
          No materials or equipment to show by task
        </div>
      ) : (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          {taskGroups.map((group) => {
            const isExpanded = expandedTasks.has(group.taskId);
            return (
              <div
                key={group.taskId}
                className="border-b border-gray-200 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleTask(group.taskId)}
                  aria-expanded={isExpanded}
                  className={cn(
                    'w-full flex items-center justify-between gap-4 px-4 py-3 transition-colors',
                    'hover:bg-gray-50',
                    isExpanded && 'bg-gray-50',
                  )}>
                  <div className="flex items-center gap-2 min-w-0">
                    {isExpanded ? (
                      <ChevronDown
                        size={16}
                        className="text-gray-500 flex-shrink-0"
                      />
                    ) : (
                      <ChevronRight
                        size={16}
                        className="text-gray-500 flex-shrink-0"
                      />
                    )}
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {group.taskName}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      ({group.items.length} item
                      {group.items.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="flex items-center gap-6 flex-shrink-0">
                    {showPriceColumns && (
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          Total Price
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCurrency(group.totalPrice)}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] uppercase tracking-wide text-gray-400">
                        Total Cost
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(group.totalCost)}
                      </span>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-3">
                    <DataTable
                      loading={false}
                      columns={columns}
                      data={group.items}
                      compact
                      emptyMessage="No items in this task"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {taskGroups.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-md mt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
          {showPriceColumns && (
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                Total Sales to Date
              </p>
              <p className="text-sm font-bold text-gray-900">
                {formatCurrency(grandTotalPrice)}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Total Cost to Date
            </p>
            <p className="text-sm font-bold text-gray-900">
              {formatCurrency(grandTotalCost)}
            </p>
          </div>
          {showPriceColumns && (
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                Raw Profit
              </p>
              <p
                className={`text-sm font-bold ${
                  rawProfit >= 0 ? 'text-green-700' : 'text-red-600'
                }`}>
                {formatCurrency(rawProfit)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
